/**
 * §rbac (2026-05-08) — frontend permission gate.
 *
 * Single source of truth: ``GET /api/admin/me`` → cached via
 * react-query for the whole session. Permissions are looked up
 * per-request server-side, so changing a role on the /roles page
 * takes effect for every other operator on their next page load.
 *
 * Usage:
 *
 *   const { has } = usePermissions();
 *   if (has("orders.edit")) { ... }
 *
 *   <Can permission="orders.delete">
 *     <Button onClick={onDelete}>Delete</Button>
 *   </Can>
 */

import type { ReactNode } from "react";
import { useEffect, useMemo } from "react";
import { Navigate, useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";

import { ApiError } from "./api";
import { clearSession } from "./auth";
import { getMe, type MeResponse, type Permission } from "./rbac";
import {
  firstAccessibleRoute,
  NO_ACCESS_ROUTE,
} from "./firstAccessibleRoute";

const ME_QUERY_KEY = ["admin", "me"] as const;

export function useMe() {
  return useQuery<MeResponse>({
    queryKey: ME_QUERY_KEY,
    queryFn: getMe,
    // Permissions don't change inside a session most of the time.
    // 5-minute freshness keeps page-to-page navigation cheap; the
    // /roles + /users mutations explicitly invalidate this key
    // on save so privilege changes propagate immediately.
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
  });
}

export interface UsePermissionsResult {
  /** True while the /me request is still in flight. */
  loading: boolean;
  /** §stale-session (2026-08-10) — true when the /me probe SETTLED in a
   * state that can only mean the session is dead: a 401 (the api() layer's
   * silent refresh already failed) or any boot-time error with no cached
   * result to fall back on. Callers must route to /login, never /no-access —
   * that page is reserved for a SUCCESSFUL /me returning zero .view perms. */
  sessionInvalid: boolean;
  /** Operator's effective permission set. Empty array when loading. */
  permissions: Permission[];
  /** True if the operator has the given permission. */
  has: (permission: Permission) => boolean;
  /** True if the operator has every permission in the list. */
  hasAll: (...permissions: Permission[]) => boolean;
  /** True if the operator has at least one of the given permissions. */
  hasAny: (...permissions: Permission[]) => boolean;
  /** The current role name, if any (e.g. "Owner", "Admin"). */
  roleName: string | null;
  /** Tenant id from the operator's binding. */
  tenantId: string;
}

export function usePermissions(): UsePermissionsResult {
  // §fix-perms-gate (2026-06-21) — gate on isPending (no result yet), NOT
  // isLoading. react-query v5 derives isLoading = isPending && isFetching, so on
  // a fresh mount there's a brief pending+idle window where isLoading is FALSE
  // while data is still undefined. Route Guards (RequirePermission) that do
  // `if (loading) return null` then read EMPTY perms and bounce to /no-access
  // BEFORE /api/admin/me even fires — and that redirect is sticky, so the
  // 47-perm result that arrives a beat later is ignored. isPending stays true
  // until the first result, so guards correctly wait for perms to load.
  const { data, isPending, isError, error, isFetching } = useMe();

  return useMemo<UsePermissionsResult>(() => {
    // §stale-session (2026-08-10) — a /me fetch has THREE outcomes, and the
    // old two-state read (loading | permissions) folded the third into
    // "zero permissions": an Owner whose tokens died overnight errored the
    // fetch, read as perms=[], and was bounced to the /no-access dead end
    // instead of /login. Split them:
    //   * loading — nothing settled yet. Also true while an ERRORED query
    //     refetches with no data (post-re-login the cached error would
    //     otherwise read as perms=[] for one render — the same bug again).
    //   * sessionInvalid — settled in error with no result to stand on
    //     (boot-time failure), or a 401 (api.ts's silent refresh already
    //     failed and cleared the tokens). Dead session → /login.
    //   * otherwise — real permissions (which may be genuinely empty).
    // A background refetch that fails on a NON-401 while stale data exists
    // stays valid: a network blip must not sign the operator out.
    const loading = isPending || (data === undefined && isFetching);
    const sessionInvalid =
      !loading &&
      isError &&
      (data === undefined ||
        (error instanceof ApiError && error.status === 401));
    const permissions = (data?.permissions ?? []) as Permission[];
    const set = new Set<string>(permissions);
    const has = (p: Permission) => set.has(p);
    return {
      loading,
      sessionInvalid,
      permissions,
      has,
      hasAll: (...ps: Permission[]) => ps.every((p) => set.has(p)),
      hasAny: (...ps: Permission[]) => ps.some((p) => set.has(p)),
      roleName: data?.role?.name ?? null,
      tenantId: data?.tenant_id ?? "",
    };
  }, [data, isPending, isError, error, isFetching]);
}

interface CanProps {
  /** Required permission. Hides children when the operator lacks it. */
  permission?: Permission;
  /** Any of these is sufficient. */
  any?: Permission[];
  /** All of these are required. */
  all?: Permission[];
  /** Optional fallback when the operator doesn't have access. */
  fallback?: ReactNode;
  children: ReactNode;
}

interface RequirePermissionProps {
  permission: Permission;
  /** Override the auto-resolved redirect destination. */
  redirectTo?: string;
  children: ReactNode;
}

/**
 * Route-level permission guard. Wrap a route element so direct URL
 * access (typing the path, bookmarks) is also gated, not just the
 * sidebar nav.
 *
 *   <Route path="users" element={
 *     <RequirePermission permission="operators.view"><UsersPage /></RequirePermission>
 *   } />
 *
 * While ``/me`` is loading we render nothing (brief flash) so the
 * page doesn't render gated content for an operator who lacks the
 * permission. On a missing permission we redirect to the first
 * sidebar route the operator can view (resolved via
 * :func:`firstAccessibleRoute`) — never to a hardcoded path the
 * operator might also lack access to (e.g. /overview for a Support-
 * only user). Operators with nothing land on /no-access.
 */
export function RequirePermission({
  permission,
  redirectTo,
  children,
}: RequirePermissionProps) {
  const { loading, sessionInvalid, has, permissions } = usePermissions();
  const location = useLocation();

  // §stale-session (2026-08-10) — clear in an effect, not during render:
  // clearSession dispatches SESSION_CLEARED_EVENT, and firing that mid-render
  // would setState inside RequireAuth while another component renders.
  useEffect(() => {
    if (sessionInvalid) clearSession();
  }, [sessionInvalid]);

  if (loading) return null;
  // §stale-session — an errored /me is a DEAD SESSION, not a zero-permission
  // role. clearSession is idempotent (on a 401 api.ts already cleared + set
  // the "session expired" banner flag that /login consumes). The intended
  // path rides along as location state, same shape RequireAuth sends.
  if (sessionInvalid) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (!has(permission)) {
    const target = redirectTo ?? firstAccessibleRoute(permissions);
    // Avoid Navigate-loops when the resolved target itself doesn't
    // grant ``permission`` (the redirected route's own Guard would
    // bounce the operator again). The /no-access page is plain
    // (no Guard) so it's a safe terminal stop.
    if (target === location.pathname) {
      return <Navigate to={NO_ACCESS_ROUTE} replace />;
    }
    return <Navigate to={target} state={{ from: location }} replace />;
  }
  return <>{children}</>;
}

/**
 * Conditionally render children based on permissions.
 *
 *   <Can permission="orders.edit">…</Can>
 *   <Can any={["orders.create", "orders.edit"]}>…</Can>
 *   <Can all={["roles.view", "roles.edit"]}>…</Can>
 *
 * While ``/me`` is still loading, children are hidden — the brief
 * flash of "no access" UI for an admin is preferable to flashing
 * gated UI to a viewer.
 */
export function Can({
  permission,
  any,
  all,
  fallback = null,
  children,
}: CanProps) {
  const { loading, has, hasAny, hasAll } = usePermissions();
  if (loading) return null;
  let allowed = true;
  if (permission && !has(permission)) allowed = false;
  if (any && !hasAny(...any)) allowed = false;
  if (all && !hasAll(...all)) allowed = false;
  return allowed ? <>{children}</> : <>{fallback}</>;
}

/**
 * §stale-session (2026-08-10) — boot gate between RequireAuth (a token
 * EXISTS) and the shell (the token WORKS). RequireAuth only proves there's
 * a token string in localStorage; an Owner reopening the app after the
 * refresh token died used to get a beat of empty dashboard chrome (sidebar
 * with zero nav items — perms still loading), then a bounce to /no-access.
 *
 * This gate holds a neutral splash (same frame as App.tsx's Suspense
 * fallback) until the /me probe settles, then:
 *   * session dead (401 / boot-time error) → clearSession + /login
 *   * session live → render the shell with permissions already cached, so
 *     an empty-sidebar frame is never committed.
 *
 * Known trade-off: within the persisted-cache window (<24h) a hydrated /me
 * renders the shell instantly from cache; if the background refetch then
 * 401s, the operator sees the shell briefly before landing on /login. That
 * is the stale-while-revalidate deal the rest of the app already makes.
 */
export function SessionGate({ children }: { children: ReactNode }) {
  const { loading, sessionInvalid } = usePermissions();
  const location = useLocation();

  // Same rationale as RequirePermission: clearSession dispatches an event,
  // so it must not run during render.
  useEffect(() => {
    if (sessionInvalid) clearSession();
  }, [sessionInvalid]);

  if (loading) {
    return (
      <div
        aria-busy="true"
        style={{
          position: "fixed",
          inset: 0,
          background: "var(--bg-1, #0a0a0c)",
          pointerEvents: "none",
        }}
      />
    );
  }
  if (sessionInvalid) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}
